// services/frontend/src/components/photos/FileDropZone.tsx
// T017-T020: FileDropZone component with HTML5 drag-drop handlers
// Supports drag/drop of both individual files and folders

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import logger from '@/utils/logger';
import { filterValidFiles, getInvalidFilesSummary } from '@/utils/fileValidation';
import { extractFilesFromDataTransfer } from '@/utils/fileExtraction';
import { cn } from '@/utils/cn';

// Hint box dimensions for viewport clamping calculations
// These approximate the rendered size based on current content and styling
const HINT_BOX = {
  width: 340,   // Approximate width with px-6 padding and text content
  height: 80,   // Approximate height with py-4 padding and two text lines
  padding: 16,  // Minimum distance from viewport edges (follows 4/8/16/24/32 spacing scale)
} as const;

export interface FileDropZoneProps {
  /** Callback when valid files are dropped */
  onFilesDropped: (files: File[]) => void;
  /** Child components to wrap with drop zone */
  children: React.ReactNode;
  /** Disable drop zone functionality */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Callback for invalid file notifications (optional) */
  onInvalidFiles?: (summary: string) => void;
}

/**
 * FileDropZone - Wrapper component that adds drag-and-drop file upload capability
 * T017: HTML5 drag-drop handlers
 * T018: Drag-over visual feedback (border highlight, overlay text)
 * T019: File drop handler with validation integration
 * T020: Invalid file filtering and user notification
 */
export function FileDropZone({
  onFilesDropped,
  children,
  disabled = false,
  className,
  onInvalidFiles
}: FileDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  // Counter to track nested drag enter/leave events from child elements
  const dragCounterRef = useRef(0);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  /**
   * Listen for document-level drag events to detect when drag leaves browser window
   * When dragging outside the browser, dragleave fires with relatedTarget = null
   */
  useEffect(() => {
    const handleDocumentDragLeave = (e: DragEvent) => {
      // relatedTarget is null when leaving the browser window entirely
      if (e.relatedTarget === null) {
        dragCounterRef.current = 0;
        setIsDragOver(false);
      }
    };

    // Also handle drop on document (in case user drops outside our zone)
    const handleDocumentDrop = () => {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    };

    document.addEventListener('dragleave', handleDocumentDragLeave);
    document.addEventListener('drop', handleDocumentDrop);

    return () => {
      document.removeEventListener('dragleave', handleDocumentDragLeave);
      document.removeEventListener('drop', handleDocumentDrop);
    };
  }, []);

  /**
   * T017: Handle drag over event - enable drop
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      // Only show drag-over state for file drops (not internal drag operations)
      if (e.dataTransfer.types.includes('Files')) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  /**
   * Handle drag enter event - increment counter for nested elements
   */
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      dragCounterRef.current++;

      if (e.dataTransfer.types.includes('Files')) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  /**
   * Handle drag leave event - decrement counter and only hide when fully left
   */
  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounterRef.current--;

      // Only clear drag-over state when counter reaches 0
      // (meaning we've left all nested elements)
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setIsDragOver(false);
      }
    },
    []
  );

  /**
   * T019: Handle file drop event with validation integration
   * T020: Invalid file filtering and user notification
   * Supports both files and folders (folders are recursively traversed)
   */
  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset drag state
      dragCounterRef.current = 0;
      setIsDragOver(false);

      if (disabled) return;

      // Extract files from data transfer (supports folders via webkitGetAsEntry)
      const droppedFiles = await extractFilesFromDataTransfer(e.dataTransfer);

      if (droppedFiles.length === 0) {
        return;
      }

      // T019: Validate files
      const { valid, invalid } = filterValidFiles(droppedFiles);

      // T020: Notify about invalid files
      if (invalid.length > 0) {
        const summary = getInvalidFilesSummary(invalid);

        if (onInvalidFiles) {
          onInvalidFiles(summary);
        } else {
          // Fallback to structured logging if no callback provided
          logger.warn('FileDropZone', '[FileDropZone] Invalid files skipped:', summary);
        }
      }

      // T019: Pass valid files to callback
      if (valid.length > 0) {
        onFilesDropped(valid);
      }
    },
    [disabled, onFilesDropped, onInvalidFiles]
  );

  // Track drop zone rect for hint positioning (updated on drag enter)
  const [dropZoneRect, setDropZoneRect] = useState<DOMRect | null>(null);

  // Update rect when drag starts to capture current position
  useEffect(() => {
    if (isDragOver && dropZoneRef.current) {
      setDropZoneRect(dropZoneRef.current.getBoundingClientRect());
    }
  }, [isDragOver]);

  // Calculate hint position clamped to viewport bounds
  // Using useMemo avoids the (0,0) flash that would occur with useEffect + useState
  const hintPosition = useMemo(() => {
    if (!dropZoneRect) {
      // Fallback: center of viewport (only used briefly before rect is captured)
      return {
        top: window.innerHeight / 2 - HINT_BOX.height / 2,
        left: window.innerWidth / 2 - HINT_BOX.width / 2,
      };
    }

    // Center the hint on the drop zone
    let left = dropZoneRect.left + dropZoneRect.width / 2 - HINT_BOX.width / 2;
    let top = dropZoneRect.top + dropZoneRect.height / 2 - HINT_BOX.height / 2;

    // Clamp to viewport bounds with padding
    left = Math.max(HINT_BOX.padding, Math.min(left, window.innerWidth - HINT_BOX.width - HINT_BOX.padding));
    top = Math.max(HINT_BOX.padding, Math.min(top, window.innerHeight - HINT_BOX.height - HINT_BOX.padding));

    return { top, left };
  }, [dropZoneRect]);

  return (
    <div
      ref={dropZoneRef}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn('relative flex flex-col h-full min-h-0', className)}
      data-testid="file-drop-zone"
    >
      {/* T018: Drag-over visual feedback */}
      {isDragOver && (
        <>
          {/* Overlay on the drop zone itself */}
          <div
            className={cn(
              'absolute inset-0 z-50',
              'bg-primary/10 backdrop-blur-sm',
              'border-4 border-dashed border-primary rounded-lg',
              'pointer-events-none'
            )}
          />
          {/* Hint box - fixed position clamped to viewport bounds
              z-[60] ensures hint appears above the overlay (z-50) */}
          <div
            className="fixed z-[60] bg-surface-1 px-6 py-4 rounded-lg shadow-lg border-2 border-primary pointer-events-none"
            style={{ top: hintPosition.top, left: hintPosition.left }}
          >
            <p className="text-lg font-semibold text-primary">
              📂 Drop files or folders here to upload
            </p>
            <p className="text-sm text-muted mt-1">
              JPEG, PNG, and TIFF files supported (max 500MB each)
            </p>
          </div>
        </>
      )}

      {children}
    </div>
  );
}
